import { useQueryClient, type QueryClient } from '@tanstack/react-query'

import { patchFromTaskRead, resolveTaskStageForCompany } from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type { TaskRead } from '@/shared/api/contracts'
import { qk } from '@/shared/api/query-keys'
import { useReorderMutation } from '@/shared/hooks/use-reorder-mutation'
import { useT } from '@/shared/i18n'
import { moveByNeighbors } from '@/shared/lib/rank'
import type { Stage, Task } from '@/shared/types/domain'

import { stageSetsQuery, useTaskStages } from '@/features/projects/api'

/**
 * Mover una tarea: cambia su posición y, si cruzó de columna, su estado.
 *
 * **Un solo rank gobierna las dos vistas** (plan 2.3). Subir una tarjeta en "En
 * curso" la sube también en la lista priorizada del proyecto, porque es
 * literalmente el mismo campo y la misma query. Que lista y tablero no se
 * contradigan no es una sincronización: es no tener dos fuentes.
 *
 * El contrato con el backend (`POST /tasks/{id}/move` con vecinos, el rank lo
 * calcula el server) está documentado en `shared/hooks/use-reorder-mutation.ts`.
 */
export interface MoveTaskVariables {
  id: string
  /** La tarea que queda ARRIBA. `null` = primer puesto. */
  beforeId: string | null
  /** La tarea que queda ABAJO. `null` = último puesto. */
  afterId: string | null
  /** Solo cuando el drop cambió de columna. */
  stage?: Stage
}

const TASK_TREES = [qk.tasks.all, qk.dueSoon.all] as const

/**
 * El orden que ya trae el backend, materializado en el ordinal `position`.
 *
 * Antes esto comparaba `priority_rank` por bytes. El listado del backend **no
 * devuelve el rank**: lo calcula, ordena con él y expone el `ROW_NUMBER()`
 * resultante como `position`. Ordenar por `position` es ordenar por lo mismo,
 * con el dato que efectivamente llega. Se aplica al llegar del API; después la
 * cache ES el orden.
 */
export function sortTasks(tasks: readonly Task[]): Task[] {
  return [...tasks].sort(
    (a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER),
  )
}

function isTaskList(value: unknown): value is Task[] {
  return (
    Array.isArray(value) &&
    (value.length === 0 ||
      (typeof value[0] === 'object' &&
        value[0] !== null &&
        'stage' in value[0] &&
        'title' in value[0]))
  )
}

function patchTaskLists(
  client: QueryClient,
  id: string,
  update: (tasks: Task[], key: readonly unknown[]) => Task[],
): void {
  for (const key of TASK_TREES) {
    client.setQueriesData({ queryKey: key }, (old: unknown) => {
      if (!isTaskList(old)) return old
      if (!old.some((task) => task.id === id)) return old
      return update(old, key)
    })
  }
}

/** La tarjeta cambió de columna: el estado y el tachado se ven en el acto. */
function withStage(task: Task, stage: Stage | undefined): Task {
  if (!stage) return task

  // La categoría dice DÓNDE está la tarjeta; `completed_at` dice CUÁNDO se
  // terminó. Soltar en "Hecho" tiene que tachar la fila de la lista en el mismo
  // frame, no cuando conteste el server.
  return {
    ...task,
    stage,
    completed_at:
      stage.category === 'done' ? (task.completed_at ?? new Date().toISOString()) : null,
  }
}

function applyTaskMove(
  client: QueryClient,
  { id, beforeId, afterId, stage }: MoveTaskVariables,
): void {
  patchTaskLists(client, id, (tasks, key) => {
    // `/due-soon` está ordenada por fecha de vencimiento, no por rank: ahí el
    // movimiento no se ve, solo el cambio de estado. Reordenarla sería inventar
    // un orden que esa pantalla no usa.
    const ordered = key === qk.dueSoon.all ? tasks : moveByNeighbors(tasks, id, beforeId, afterId)
    return ordered.map((task) => (task.id === id ? withStage(task, stage) : task))
  })
}

/**
 * Llega la respuesta: la posición autoritativa entra en su lugar, sin reordenar.
 *
 * Se PARCHEA, no se reemplaza. `POST /tasks/{id}/move` devuelve `TaskRead`, que
 * es más chico que la fila del listado (no trae el nombre del proyecto ni el de
 * la empresa): pisar la fila entera con él dejaría la tabla sin la mitad de sus
 * columnas justo después de un drop.
 */
function reconcileTask(client: QueryClient, moved: TaskRead, stages: readonly Stage[]): void {
  if (!moved?.id) return

  const patch = patchFromTaskRead(moved, stages)
  patchTaskLists(client, moved.id, (tasks) =>
    tasks.map((task) => (task.id === moved.id ? { ...task, ...patch } : task)),
  )
}

/** La empresa de la tarea, leída de cualquier lista cacheada que la tenga. */
function cachedCompanyId(client: QueryClient, id: string): string | null {
  for (const key of TASK_TREES) {
    for (const [, data] of client.getQueriesData({ queryKey: key })) {
      if (!isTaskList(data)) continue
      const task = data.find((candidate) => candidate.id === id)
      if (task?.project?.company?.id) return task.project.company.id
    }
  }
  return null
}

/**
 * El `stage_id` que acepta el backend: el de la MISMA columna en el set de
 * tareas de la empresa de la tarea (ver `resolveTaskStageForCompany`).
 *
 * Si el set de la empresa no está en cache —el backend lo clona la primera vez
 * que hace falta—, se pide de nuevo una vez antes de rendirse.
 */
async function stageIdForTask(client: QueryClient, taskId: string, stage: Stage): Promise<string> {
  const companyId = cachedCompanyId(client, taskId)
  if (!companyId) return stage.id

  const cached = await client.ensureQueryData(stageSetsQuery('task'))
  const resolved = resolveTaskStageForCompany(cached, companyId, stage.id)
  if (resolved) return resolved

  const fresh = await client.fetchQuery({ ...stageSetsQuery('task'), staleTime: 0 })
  return resolveTaskStageForCompany(fresh, companyId, stage.id) ?? stage.id
}

export function useMoveTask() {
  const client = useQueryClient()
  const stages = useTaskStages()
  const t = useT()

  return useReorderMutation<MoveTaskVariables, TaskRead>({
    queryKeys: TASK_TREES,
    invalidateOnSettled: [qk.portfolio.all, qk.projects.all],
    apply: applyTaskMove,
    reconcile: (client, data) => reconcileTask(client, data, stages.data ?? []),
    mutationFn: async ({ id, beforeId, afterId, stage }) =>
      api.post<TaskRead>(`tasks/${id}/move`, {
        before_id: beforeId,
        after_id: afterId,
        ...(stage ? { stage_id: await stageIdForTask(client, id, stage) } : {}),
      }),
    errorText: t('tasks.error.move'),
    entity: 'task',
  })
}
